<template>
    <div :class="{ 'height-95pct': openInfoEquipement }" class="step-three-container">
        <div class="title">
            <span>
                {{ $t('equipementSelection.search.title') }}
            </span>
            <ReglePastille cle="demandeIntervention.rechercheEquipementCriteres" />
            <ReglePastille cle="demandeIntervention.etatEquipementObligatoire" />
        </div>
        <div class="search-option-container">
            <div class="search-bar-container">
                <input
                    id="equipement-filter"
                    v-model="equipementFilter"
                    class="search-input"
                    placeholder=" "
                    required
                    type="text"
                />
                <label class="label" for="equipement-filter">{{
                    $t('equipementSelection.search.equipement.placeholder')
                }}</label>
            </div>
            <div class="search-bar-container">
                <input
                    id="groupe-filter"
                    v-model="groupeFilter"
                    class="search-input"
                    placeholder=" "
                    required
                    type="text"
                />
                <label class="label" for="groupe-filter">{{
                    $t('equipementSelection.search.groupe.placeholder')
                }}</label>
            </div>
            <div class="search-bar-container">
                <input
                    id="ssgroupe-filter"
                    v-model="ssGroupeFilter"
                    class="search-input"
                    placeholder=" "
                    required
                    type="text"
                />
                <label class="label" for="ssgroupe-filter">{{
                    $t('equipementSelection.search.ssgroupe.placeholder')
                }}</label>
            </div>
            <TickBox
                v-if="displayRadio"
                :aria-checked="equipement === null"
                :aria-label="$t('equipementSelection.search.none')"
                :selected="equipement === null"
                class="tick"
                role="checkbox"
                tabindex="0"
                @click="equipementMissing"
                @keydown.enter="equipementMissing"
                @keydown.space.prevent="equipementMissing"
            />
            <span v-if="displayRadio" aria-hidden="true"> {{ $t('equipementSelection.search.none') }} </span>
            <ReglePastille v-if="displayRadio" cle="demandeIntervention.equipementNonTrouve" />
            <div class="btnWrapper">
                <div class="btns">
                    <slot name="extra-actions" />
                    <ButtonWhite :onClick="clearFilter" :text="$t('button.clear')" class="clearBtn" />
                    <ButtonBlue
                        :disabled="disabled()"
                        :onClick="search"
                        :text="$t('button.search')"
                        class="searchBtn"
                    />
                </div>
            </div>
        </div>

        <div ref="equipementDiv" class="equipement-list" @scroll="handleScroll">
            <EquipementList
                :display-radio="displayRadio"
                :equipements="equipementList"
                :multiple="multiple"
                :open-info-equipement="openInfoEquipement"
                :selected-ids="selectedIds"
                :excluded-equipement-ids="excludedEquipementIds"
                :allow-select-all="allowSelectAll"
                :equipement-namespace="equipementNamespace"
                @sort="doSort"
                @update:selectedIds="$emit('update:selectedIds', $event)"
            />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, PropType, ref } from 'vue';
import EquipementList from '@/modules/Intervention/components/EquipementList.vue';
import TickBox from '@/components/ui-components/TickBox.vue';
import { EquipementActions } from '../store/modules/Equipement';
import { PreavisEquipementActions } from '@/modules/PreavisIntervention/store/modules/PreavisEquipement';
import { EQUIPEMENT, INTERVENTION } from '../store/mutation-types';
import { store } from '@/plugins/store';
import ButtonWhite from '@/components/ui-components/ButtonWhite.vue';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface StepThreeData {
    debounce: number;
}

export default defineComponent({
    components: {
        EquipementList,
        TickBox,
        ButtonBlue,
        ButtonWhite,
        ReglePastille,
    },
    props: {
        displayRadio: { type: Boolean, required: true },
        openInfoEquipement: { type: Boolean, required: true },
        multiple: { type: Boolean, default: false },
        selectedIds: { type: Array as PropType<number[]>, default: () => [] },
        excludedEquipementIds: { type: Array as PropType<string[]>, default: () => [] },
        allowSelectAll: { type: Boolean, default: false },
        equipementNamespace: { type: String, default: 'equipementModule' },
        siteGetter: { type: String, default: 'getInterventionSiteValue' },
        equipementGetter: { type: String, default: 'getInterventionEquipementValue' },
        setEquipementMutation: { type: String, default: INTERVENTION.SET_EQUIPEMENT },
    },
    emits: ['update:selectedIds'],
    setup() {
        const equipementDiv = ref<Element>();

        onMounted(() => {
            equipementDiv.value;
        });

        return {
            equipementDiv,
        };
    },
    data(): StepThreeData {
        return { debounce: 0 };
    },
    mounted() {
        const eqAction = this.equipementNamespace === 'preavisEquipementModule'
            ? PreavisEquipementActions.GET_EQUIPEMENT
            : EquipementActions.GET_EQUIPEMENT;
        const etatAction = this.equipementNamespace === 'preavisEquipementModule'
            ? PreavisEquipementActions.GET_ETAT_EQUIPEMENT
            : EquipementActions.GET_ETAT_EQUIPEMENT;
        if (this.equipementList.length === 0) {
            store.dispatch(`${this.equipementNamespace}/${eqAction}`, {
                adresseSiteId: this.site.id,
                page: 0,
                equipementFilter: this.equipementFilter,
                groupeFilter: this.groupeFilter,
                ssGroupeFilter: this.ssGroupeFilter,
                sort: store.getters[`${this.equipementNamespace}/getSort`],
            });
        }
        this.equipementDiv?.scrollTo({ top: this.getScroll });
        store.dispatch(`${this.equipementNamespace}/${etatAction}`);
    },
    watch: {
        'site.id'(newId: number, oldId: number) {
            if (newId && newId !== oldId) {
                const eqAction = this.equipementNamespace === 'preavisEquipementModule'
                    ? PreavisEquipementActions.GET_EQUIPEMENT
                    : EquipementActions.GET_EQUIPEMENT;
                store.commit(`${this.equipementNamespace}/SET_EQUIPEMENT_SCROLL`, 0 as any);
                store.commit(`${this.equipementNamespace}/SET_EQUIPEMENT_ITEMS`, [] as any);
                store.commit(`${this.equipementNamespace}/SET_EQUIPEMENT_NUMBER_OF_PAGES`, 0 as any);
                store.commit(`${this.equipementNamespace}/SET_EQUIPEMENT_PAGE`, 0 as any);
                store.commit(`${this.equipementNamespace}/SET_EQUIPEMENT_LOADING`, true as any);
                store.commit(`${this.equipementNamespace}/SET_EQUIPEMENT_FILTER`, '' as any);
                store.commit(`${this.equipementNamespace}/SET_EQUIPEMENT_GROUPE_FILTER`, '' as any);
                store.commit(`${this.equipementNamespace}/SET_EQUIPEMENT_SS_GROUPE_FILTER`, '' as any);
                store.commit(`${this.equipementNamespace}/SET_EQUIPEMENT_SORT`, {
                    sortData: 'equipementLibelle',
                    sortStatus: 0,
                } as any);
                if (this.setEquipementMutation) {
                    store.commit(this.setEquipementMutation, undefined);
                }
                store.dispatch(`${this.equipementNamespace}/${eqAction}`, {
                    adresseSiteId: newId,
                    page: 0,
                    equipementFilter: '',
                    groupeFilter: '',
                    ssGroupeFilter: '',
                    sort: store.getters[`${this.equipementNamespace}/getSort`],
                });
            }
        },
    },
    computed: {
        equipementList() {
            return store.getters[`${this.equipementNamespace}/getEquipementList`];
        },
        equipementFilter: {
            get() {
                return store.getters[`${this.equipementNamespace}/getEquipementFilter`];
            },
            set(value: string) {
                this.$store.commit(`${this.equipementNamespace}/${EQUIPEMENT.SET_EQUIPEMENT_FILTER}`, value);
            },
        },
        ssGroupeFilter: {
            get() {
                return store.getters[`${this.equipementNamespace}/getSsGroupeFilter`];
            },
            set(value: string) {
                this.$store.commit(`${this.equipementNamespace}/${EQUIPEMENT.SET_SS_GROUPE_FILTER}`, value);
            },
        },
        groupeFilter: {
            get() {
                return store.getters[`${this.equipementNamespace}/getGroupeFilter`];
            },
            set(value: string) {
                this.$store.commit(`${this.equipementNamespace}/${EQUIPEMENT.SET_GROUPE_FILTER}`, value);
            },
        },
        equipement() {
            return store.getters[this.equipementGetter];
        },
        site() {
            return store.getters[this.siteGetter];
        },
        page() {
            return store.getters[`${this.equipementNamespace}/getPage`];
        },
        numberOfPages() {
            return store.getters[`${this.equipementNamespace}/getNumberOfPages`];
        },
        isLoading() {
            return store.getters[`${this.equipementNamespace}/getIsLoading`];
        },
        getScroll() {
            return store.getters[`${this.equipementNamespace}/getScroll`];
        },
    },
    methods: {
        getEquipementAction(): string {
            return this.equipementNamespace === 'preavisEquipementModule'
                ? PreavisEquipementActions.GET_EQUIPEMENT
                : EquipementActions.GET_EQUIPEMENT;
        },
        disabled(): boolean {
            return !(this.equipementFilter !== '' || this.groupeFilter !== '' || this.ssGroupeFilter !== '');
        },
        doSort() {
            store.commit(`${this.equipementNamespace}/${EQUIPEMENT.SET_ITEMS}`, []);
            store.dispatch(`${this.equipementNamespace}/${this.getEquipementAction()}`, {
                adresseSiteId: this.site.id,
                page: 0,
                equipementFilter: this.equipementFilter,
                groupeFilter: this.groupeFilter,
                ssGroupeFilter: this.ssGroupeFilter,
                sort: store.getters[`${this.equipementNamespace}/getSort`],
            });
        },
        equipementMissing() {
            if (this.setEquipementMutation) {
                store.commit(this.setEquipementMutation, this.equipement === null ? undefined : null);
            }
        },
        handleScroll() {
            const top = this.equipementDiv?.scrollTop ?? 0;
            const max = this.equipementDiv?.scrollHeight ?? 0;
            const height = this.equipementDiv?.clientHeight ?? 0;

            if (max - top <= height * 1.5 && this.page + 1 < this.numberOfPages && !this.isLoading) {
                store.dispatch(`${this.equipementNamespace}/${this.getEquipementAction()}`, {
                    adresseSiteId: this.site.id,
                    filter: this.equipementFilter,
                    page: this.page + 1,
                    equipementFilter: this.equipementFilter,
                    groupeFilter: this.groupeFilter,
                    ssGroupeFilter: this.ssGroupeFilter,
                    sort: store.getters[`${this.equipementNamespace}/getSort`],
                });
            }
        },
        clearFilter(): void {
            store.commit(`${this.equipementNamespace}/${EQUIPEMENT.SET_ITEMS}`, []);
            this.equipementFilter = '';
            this.groupeFilter = '';
            this.ssGroupeFilter = '';
            this.search();
        },
        search(): void {
            store.commit(`${this.equipementNamespace}/${EQUIPEMENT.SET_ITEMS}`, []);
            store.dispatch(`${this.equipementNamespace}/${this.getEquipementAction()}`, {
                adresseSiteId: this.site.id,
                page: 0,
                equipementFilter: this.equipementFilter,
                groupeFilter: this.groupeFilter,
                ssGroupeFilter: this.ssGroupeFilter,
                sort: store.getters[`${this.equipementNamespace}/getSort`],
            });
        },
    },
});
</script>

<style scoped>
.equipement-list {
    overflow-y: auto;
}
.step-three-container {
    height: calc(100vh - 280px);
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 20px;
    background-color: var(--white);
    box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px rgba(0, 0, 0, 0.14), 0 1px 5px rgba(0, 0, 0, 0.12);
    border-radius: 4px;
}
.search-option-container {
    display: flex;
    align-items: center;
    margin-top: 8px;
    margin-bottom: 18px;
}
.tick {
    margin-right: 8px;
}
.search-bar-container {
    height: 16px;
    width: 20%;
    position: relative;
    display: flex;
    border: 1px solid var(--medium-dark-grey);
    border-radius: 4px;
    padding: 11px 12px;
    align-items: center;
    margin-right: 15px;
}
@media (max-width: 1000px) {
    .search-bar-container {
        width: 50%;
        margin-bottom: 5px;
    }
    .search-option-container {
        flex-direction: column;
    }
}
@media (min-width: 1001px) and (max-width: 1300px) {
    .search-input:placeholder-shown:not(:focus) + .label {
        font-size: 12px !important;
    }
}
.search-input {
    font-family: 'Roboto';
    border: none;
    outline: none;
    flex: 1;
    color: var(--black);
    order: 2;
}
.search-input:placeholder-shown:not(:focus) + .label {
    top: calc(50% - var(--font-size-low-medium) / 2);
    font-size: var(--font-size-low-medium);
}
.label {
    font-family: 'Roboto';
    position: absolute;
    pointer-events: none;
    left: 12px;
    top: -7px;
    transition: 0.2s ease all;
    padding: 2px;
    background-color: var(--white);
    color: var(--medium-dark-grey);
    font-size: var(--font-size-very-small);
    order: 1;
}
.btns {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-bottom: 8px;
    gap: 8px;
}
.btnWrapper {
    display: flex;
    justify-content: flex-end;
    margin-left: auto;
}
@media (min-width: 1001px) {
    .btnWrapper {
        margin-left: auto;
    }
}
@media (max-width: 1001px) {
    .btnWrapper {
        margin-top: 15px;
    }
}
.searchBtn {
    background-color: var(--color-principal);
    color: white;
    width: 120px;
}
.clearBtn,
.searchBtn {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border: 1px solid var(--color-principal);
    border-radius: 4px;
    padding: 10px;
    text-transform: uppercase;
    font-weight: 600;
    max-height: 50px;
}
.tableHeader {
    margin-bottom: 8px;
}
.height-95pct {
    height: 95%;
}
.export-button {
    float: right;
    position: relative;
    top: -15px;
}
</style>
